/**
 * Idle-timeout enforcement for authenticated sessions.
 *
 * Listens for user activity events (mouse / keyboard / touch / scroll) on
 * the window, debounces them, and tracks "last activity" in memory. A
 * single timer ticks once per minute and:
 *   - shows a warning modal at warnAtMs (default 14 min idle)
 *   - signs the user out at signOutAtMs (default 15 min idle)
 *
 * The signout flow goes through useAuthStore().logout() which already calls
 * Cognito's signOut() — with enable_token_revocation = true on the user
 * pool client, that revokes the refresh token server-side, not just local.
 *
 * Detached from React state intentionally — we use a ref + setState for the
 * "warning visible" flag only. Mouse activity at any time after the modal
 * appears auto-dismisses it (the activity handler is still running).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

const DEFAULT_WARN_MS = 14 * 60 * 1000;     // show warning at 14 min idle
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;  // sign out at 15 min idle
const TICK_MS = 30 * 1000;                  // check every 30 s

export interface IdleTimeoutOptions {
  warnAtMs?: number;
  signOutAtMs?: number;
  /** When the warning is visible, how often to recheck whether activity
   *  resumed (allows the modal to auto-dismiss without a full re-render). */
  tickMs?: number;
}

export interface IdleTimeoutState {
  /** True when the warning modal should be visible. */
  warningVisible: boolean;
  /** Milliseconds remaining until forced sign-out, rounded down. */
  msUntilSignOut: number;
  /** Reset the idle clock manually (e.g. "Stay signed in" button). */
  stayActive: () => void;
}

export function useIdleTimeout(opts: IdleTimeoutOptions = {}): IdleTimeoutState {
  const warnAt = opts.warnAtMs ?? DEFAULT_WARN_MS;
  const signOutAt = opts.signOutAtMs ?? DEFAULT_TIMEOUT_MS;
  const tick = opts.tickMs ?? TICK_MS;

  // Initial value is set on mount via effect below — using a sentinel here
  // keeps the hook compatible with strict purity rules in react-hooks.
  const lastActivityRef = useRef<number>(0);
  const [warningVisible, setWarningVisible] = useState(false);
  const [msUntilSignOut, setMsUntilSignOut] = useState(signOutAt);

  const logout = useAuthStore((s) => s.logout);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const stayActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarningVisible(false);
    setMsUntilSignOut(signOutAt);
  }, [signOutAt]);

  // Initialize the activity timestamp on first authenticated mount.
  useEffect(() => {
    if (isAuthenticated && lastActivityRef.current === 0) {
      lastActivityRef.current = Date.now();
    }
  }, [isAuthenticated]);

  // ── Activity listener ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    // Throttle: only update lastActivityRef once per 5 s to avoid thrashing.
    let lastSetAt = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastSetAt < 5000) return;
      lastSetAt = now;
      lastActivityRef.current = now;
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, handler, { passive: true });
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, handler);
      }
    };
  }, [isAuthenticated]);

  // ── Ticker: check elapsed time ───────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = signOutAt - elapsed;
      if (elapsed >= signOutAt) {
        setWarningVisible(false);
        logout();
        return;
      }
      if (elapsed >= warnAt) {
        setWarningVisible(true);
        setMsUntilSignOut(Math.max(0, remaining));
      } else if (warningVisible) {
        // Activity resumed — auto-dismiss the warning.
        setWarningVisible(false);
        setMsUntilSignOut(signOutAt);
      }
    }, tick);
    return () => clearInterval(interval);
  }, [isAuthenticated, warnAt, signOutAt, tick, logout, warningVisible]);

  // While the warning is visible, tick the countdown faster so the user sees
  // the seconds drop. Once they move the mouse the activity handler resets
  // lastActivityRef and the next normal tick will hide the modal.
  useEffect(() => {
    if (!warningVisible) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = signOutAt - elapsed;
      if (remaining <= 0) {
        setMsUntilSignOut(0);
        logout();
        return;
      }
      setMsUntilSignOut(remaining);
    }, 1000);
    return () => clearInterval(interval);
  }, [warningVisible, signOutAt, logout]);

  return { warningVisible, msUntilSignOut, stayActive };
}
